import { Link } from "react-router-dom";

export default function SublimationVsDTF() {
  return (
    <>
      <p>
        "What's the difference between sublimation and DTF?" is the single
        most common question we get from clubs and schools ordering custom
        apparel. Both are excellent printing methods. They're just suited
        to different jobs. Pick the wrong one and you'll either pay too
        much, wait too long, or end up with a kit that doesn't perform the
        way it should. Pick the right one and you'll get exactly the
        result you wanted.
      </p>
      <p>
        Here's the practical breakdown — what each method is, when to use
        each, and how to decide for your specific job.
      </p>

      <h2>What is sublimation?</h2>
      <p>
        Sublimation is a dyeing process. Your design is printed onto a
        special transfer paper using sublimation inks, then heat and
        pressure are applied to a polyester garment. The ink turns from
        solid to gas (sublimates) and bonds with the fabric fibres. The
        end result: the design <em>becomes</em> the fabric. There's no
        layer sitting on top of the shirt.
      </p>
      <p>
        Because the dye is part of the fibre, sublimated prints are
        unbeatable for durability — they can't crack, peel, fade or wash
        out the way printed-on layers can. They also don't add any weight
        or texture to the garment, so a sublimated jersey feels exactly
        like a plain jersey.
      </p>

      <h2>What is DTF (Direct-to-Film)?</h2>
      <p>
        DTF is a transfer printing process. Your design is printed onto a
        special PET film with pigment inks and a hot-melt adhesive
        powder. The film is then heat-pressed onto the garment, leaving
        the design bonded to the fabric surface. It's a modern evolution
        of older transfer techniques — much higher quality, far more
        durable, and capable of full-colour photographic detail.
      </p>
      <p>
        DTF prints sit <em>on top</em> of the fabric (you can feel the
        print with your fingertips), but the modern formulations are soft
        enough that this is barely noticeable. Wash durability is
        excellent — typically 50+ wash cycles with no degradation.
      </p>

      <h2>The big differences at a glance</h2>
      <table>
        <thead>
          <tr>
            <th>Factor</th>
            <th>Sublimation</th>
            <th>DTF</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Fabric compatibility</td>
            <td>Polyester only (light colours)</td>
            <td>Cotton, poly, blends, dark colours</td>
          </tr>
          <tr>
            <td>Hand feel</td>
            <td>None — feels like the fabric</td>
            <td>Soft layer, slight texture</td>
          </tr>
          <tr>
            <td>Coverage</td>
            <td>Full-garment, all-over patterns</td>
            <td>Localised graphics, logos, panels</td>
          </tr>
          <tr>
            <td>Durability</td>
            <td>Lifetime — won't crack/fade</td>
            <td>Excellent — 50+ wash cycles</td>
          </tr>
          <tr>
            <td>Setup cost</td>
            <td>Higher (per design)</td>
            <td>Low — no setup fee</td>
          </tr>
          <tr>
            <td>Minimum order</td>
            <td>10+ recommended</td>
            <td>None — single-shirt friendly</td>
          </tr>
          <tr>
            <td>Turnaround</td>
            <td>2–3 weeks</td>
            <td>72 hours possible</td>
          </tr>
        </tbody>
      </table>

      <h2>When to use sublimation</h2>
      <ul>
        <li>
          <strong>Team playing kits</strong> — rugby, basketball, netball,
          touch jerseys with all-over patterns or photo-quality artwork.
        </li>
        <li>
          <strong>Cultural designs with full-garment patterning</strong> —
          tatau panels, kowhaiwhai bands, siapo overlays.
        </li>
        <li>
          <strong>High-wash-cycle gear</strong> — anything that's going to
          be washed weekly for years.
        </li>
        <li>
          <strong>Performance fabrics</strong> — when you need the dye
          embedded so the technical fabric still breathes properly.
        </li>
      </ul>
      <p>
        Sublimation is what we use for{" "}
        <Link to="/custom-rugby-uniforms-nz">custom rugby uniforms</Link>{" "}
        and{" "}
        <Link to="/custom-basketball-jerseys-nz">basketball jerseys</Link>{" "}
        almost without exception.
      </p>

      <h2>When to use DTF</h2>
      <ul>
        <li>
          <strong>Event tees &amp; supporter shirts</strong> — short runs,
          fast turnaround, soft cotton blanks.
        </li>
        <li>
          <strong>Cotton garments</strong> — sublimation can't print on
          cotton, so DTF is the answer for soft cotton tees and hoodies.
        </li>
        <li>
          <strong>Dark-colour shirts</strong> — sublimation only works on
          light fabrics; DTF prints brilliantly on black, navy and any
          dark base.
        </li>
        <li>
          <strong>One-offs &amp; small runs</strong> — no minimum-order
          pressure, no setup cost.
        </li>
        <li>
          <strong>Last-minute jobs</strong> — DTF can ship in 72 hours.
        </li>
      </ul>
      <p>
        DTF is what powers our{" "}
        <Link to="/fast-tshirt-printing-auckland">fast t-shirt printing in Auckland</Link>{" "}
        service.
      </p>

      <h2>Common scenarios — which to pick</h2>
      <ul>
        <li>
          <em>"We need 18 rugby jerseys for the season."</em> →
          Sublimation.
        </li>
        <li>
          <em>"We need 60 supporter tees for our church camp this Saturday."</em> →
          DTF.
        </li>
        <li>
          <em>"We need 30 black hoodies with our cultural-group logo."</em> →
          DTF (cotton + dark colour).
        </li>
        <li>
          <em>"We need 25 basketball jerseys with full-bleed pattern panels."</em> →
          Sublimation.
        </li>
        <li>
          <em>"We need 5 staff polos for tomorrow."</em> → DTF.
        </li>
      </ul>

      <h2>Can you mix them?</h2>
      <p>
        Yes — and we often do. A typical premium club order is sublimated
        playing jerseys (full-colour all-over) <em>plus</em> DTF-printed
        cotton supporter tees and DTF-on-cotton training hoodies. You get
        the best of both methods on the right garments.
      </p>

      <h2>Need help deciding?</h2>
      <p>
        Tell us about your job and we'll recommend the right method
        before we quote. We don't have a horse in the race — we run both
        in-house — so the recommendation is based on what's right for you,
        not what's easier for us.
      </p>
      <p>
        <Link to="/custom-uniforms-auckland">
          Get a free design concept &amp; quote →
        </Link>
      </p>
    </>
  );
}
